import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { GeoLoc } from "rainbow-web-sdk/models/geoLoc.model";
import { FileDescriptor } from 'rainbow-web-sdk/models/common/fileDescriptor';
import { UrlMetaData } from "rainbow-web-sdk/services/ulrMetadata/urlMetadata.service";
import { Contact } from "./contact.model";
import { RBEvent } from "./event.model";
import { Bubble } from "./bubble.model";
import { Branded } from "../utils/branded";

export type Urgency = 'std' | 'low' | 'middle' | 'high';

export function parseUrgency(text: string): Urgency {
    switch (text) {
        case 'std':
        case 'low':
        case 'middle':
        case 'high':
            return text;
        default: return 'std';
    }
}

export class MessageAdditionalContent<Type extends string = string> {
    constructor(public type: Type, public message: string) { }
    static create<T extends string>(type: T, message: string): MessageAdditionalContent<T> { return new MessageAdditionalContent(type, message); }
}

export class ReplaceMsg {

    public id: MessageID;
    public body: string;

    public static create(id: MessageID, body: string): ReplaceMsg {
        return new ReplaceMsg(id, body);
    }

    private constructor(id: MessageID, body: string) {
        this.id = id;
        this.body = body;
    }
}

export class Mention {
    /** Mentionned user id */
    public dbId: string;
    /** Mentionned user display name */
    public displayName?: string;

    private constructor(dbId: string, displayName?: string) {
        this.dbId = dbId;
        this.displayName = displayName;
    }

    /**
     * Create a new mention from user data
     * @param id - db id of the mentionned user
     * @param displayName - user display name
     */
    static create(id: string, displayName?: string): Mention { return new Mention(id, displayName); }
}

export class MentionLegacy {
    /** Mentionned user display name */
    public displayName?: string;

    constructor(public dbId: string, public jid: string, public position: number, public size: number, displayName?: string) {
        this.displayName = displayName;
    }

    /**
     * Create a new mention from a jid, position and size
     * @param dbId - db id of the mentionned user
     * @param jid - jid of the mentionned user
     * @param position - the mention position in a message
     * @param size - the mention text length inside a message
     * @param displayName - user display name
     * @returns a mention object
     */
    static create(dbId: string, jid: string, position: number, size: number, displayName?: string) { return new MentionLegacy(dbId, jid, position, size, displayName); }
}

export class Reaction {
    /** Jid of user reacting */
    public jid: string;
    /** Concatenated reaction by user */
    public emojis: string;

    private constructor(jid: string, emojis: string) {
        this.jid = jid;
        this.emojis = emojis;
    }

    /**
     * Create a new reaction
     * @param jid - jid of user reacting
     * @param emojis - Concatenated reaction by user
     *
     * @returns a reaction object
     */
    static create(jid: string, emojis: string): Reaction { return new Reaction(jid, emojis); }
}

/**
 * Convert a contact into a mention string
 * @param contact - The user contact
 *
 * @public
 */
export function formatMention(contact: Contact | Mention): string {
    return `<@${contact.displayName}:${contact.dbId}>`;
}

export const MENTION_REGEX = /<@([^{}[\]\$#~!;*%=+\\/:]+):([^>]+)>/g;

/**
 * @public
 * @description Message side: from a recipient, from me or specific admin message
 */
export enum MessageSide {
    LEFT = "L",
    RIGHT = "R",
    ADMIN = "ADMIN"
}

/**
 * @public
 * @description Message type
 */
export enum MessageType {
    /** A chat message */
    CHAT = "Chat",
    /** A file message */
    FILE = "File",
    /** A file message */
    FS = "FileSharing",
    /** A WebRTC message */
    WEBRTC = "WebRTC CAll",
    /** A Recording message */
    RECORDING = "Recording",
    /** A Form message */
    FORM = "FORM",
    /** A Forward message */
    FORWARD = "FORWARD",
    /** A Pin message */
    PIN = "PIN",
    /** A Poll message */
    POLL = "POLL",
    /** A Poll message */
    RECAP = "RECAP"
}

export enum MessageStatus {
    /** No receipt received yet */
    NONE = 'NONE',
    /** No receipt received after a while (The server doesn't answer) */
    ERROR = 'ERROR',
    /** Receipt in progress */
    IN_PROGRESS = 'IN_PROGRESS',
    /** The server has confirmed the reception of the message */
    SENT = 'SENT',
    /** The message has been received but not read */
    UNREAD = 'UNREAD',
    /** The message has been read */
    READ = 'READ'
}

type MessageWithAdditional<ContentType extends string = string> = Message & {
    additionalContent: NonNullable<MessageAdditionalContent<ContentType>>;
};

export type MessageID = Branded<string, 'messageId'>;

export class Message {

    public rxSubject: Subject<any> = new Subject<RBEvent | undefined>();
    private receiptStatusInfo = {
        NONE: { rank: 0, icon: 'close' },
        ERROR: { rank: 1, icon: 'error--filled' },
        IN_PROGRESS: { rank: 2, icon: 'calllog' },
        SENT: { rank: 3, icon: 'check' },
        UNREAD: { rank: 4, icon: 'done' },
        READ: { rank: 5, icon: 'read' }
    };

    /**
     * The message ID
     * @readonly
     */
    public id: MessageID;

    /**
     * The type of the message
     * @readonly
     */
    public type: MessageType;

    /**
     * The creation date of the message
     * @readonly
     */
    public date: Date;

    /**
     * The initiator of the message
     * @readonly
     */
    public from: Contact;

    /**
     * The receiver of the message
     * @readonly
     */
    public to: Contact | Bubble;

    /**
     * The message originator
     * @readonly
     */
    public side: MessageSide;

    /**
     * The content of the message
     * @readonly
     */
    public data: string;

    /**
     * The status of the send
     * @readonly
     */
    public sendStatus: MessageStatus = MessageStatus.NONE;

    /**
     * The status of the receipt
     * @readonly
     */
    public receiptStatus: MessageStatus = MessageStatus.NONE;

    /**
     * An attached file Id (if exists)
     * @readonly
     */
    public fileId: string;

    /**
     * An attached file Name (if exists)
     * @readonly
     */
    public fileName: string;

    /**
     * If the message has additional contents
     * @readonly
     */
    public additionalContent?: MessageAdditionalContent;

    /**
     * @public
     * @property {string} subject The subject of the message displayed in notifs
     * @readonly
     */
    public subject: string;

    /**
     * @public
     * @property {string} answeredMsgId The Id of the message answered
     * @readonly
     */
    public answeredMsgId?: MessageID;

    /**
     * @public
     * @property {string} answeredMsgDate The Date of the message answered
     * @readonly
     */
    public answeredMsgDate: Date;

    /**
     * @public
     * @property {GeoLoc} geoloc
     * @readonly
     */
    public geoloc: GeoLoc;

    /**
     * @public
     * @property {any} voiceMessage
     * @readonly
     */
    public voiceMessage: any;

    /**
     * @public
     * @readonly
     */
    public mentions: (Mention | MentionLegacy)[] = [];
    public mentionedMembersArray: (Mention | MentionLegacy)[] = [];

    /**
     * Map representing the relation jid / reaction
     *
     * @type {Map<string, Reaction>}
     * @memberof Message
     */
    public reactions: Map<string, Reaction>;
    /**
     * Map representing the relation emoji / count
     *
     * @type {Map<string, number>}
     * @memberof Message
     */
    public reactionsCount: Map<string, number>;
    public reactionsSubject = new Subject<{ reactions: Map<string, Reaction>; addedReaction: Reaction; }>();

    /**
     * @public
     * @property {UrlMetaData|null} urlMetadata
     * @readonly
     */
    public urlMetadata: UrlMetaData | null = null;

    /**
     * @private
     * @readonly
     */
    public serverAckTimer: any = null;

    /**
     * The urgency of message ('std', 'low', 'middle', 'high')
     * @readonly
     */
    public urgency: Urgency;
    public urgencyAck: boolean = false;
    public urgencyAckType: string = null;
    public urgencyHandler: any = null;
    public translatedText: string = null;
    public modified: boolean = false;
    public deleted: boolean = false;

    public lastReadSubject = new BehaviorSubject<any>(null);
    public isMerged: boolean = false;

    public historyIndex: any = null;
    public showCorrectedMessages: boolean = false;
    public replaceMsgs: any[] = [];
    public fileErrorMsg: string = null;
    public shortFileDescriptor: FileDescriptor = null; //MCO ?????? addd for SDK

    // Message Attachment Part
    public attachedMsgId: MessageID | null = null;
    public attachIndex?: number;
    public attachNumber?: number;

    // Message Forwarded
    public forwardedMsg: Message = null;
    public isForwarded: boolean;
    public forwardedInfo: any;
    public forwardConvType: string;
    public showIdentity: boolean = false;

    //in case we want the server not to store the message in the DB, but only send it 'one-time' to the other party
    public noStore: boolean = false;

    // CCH: maximum reactions allowed per user for a given message. Currently here but should be moved if it becomes configurable
    public static maxReactionperUser: number = 10;

    private constructor(
        id: MessageID,
        date: Date,
        from: any,
        side: MessageSide,
        data: string,
        fileId: string = null,
        additionalContent: MessageAdditionalContent = null,
        subject: string = null,
        fileName: string = null,
        answeredMsgId?: MessageID,
        answeredMsgDate: Date | null = null,
        geoloc: GeoLoc = null,
        mentions: Mention[] = []) {

        this.id = id;
        this.date = date;
        this.from = from;
        this.side = side;
        this.data = data;
        this.fileId = fileId;
        this.additionalContent = additionalContent;
        this.subject = subject;
        this.fileName = fileName;
        this.answeredMsgId = answeredMsgId;
        this.answeredMsgDate = answeredMsgDate;
        this.geoloc = geoloc;
        this.mentions = mentions;
        this.reactions = new Map();
        this.reactionsCount = new Map();
    }

    public static create(id: MessageID, date: Date, from: any, side: MessageSide, data: string,
        additionalContent?: MessageAdditionalContent, subject?: any, answeredMsgId?: MessageID, answeredMsgDate?: Date | null, mentions: Mention[] = []): Message {
        const message = new Message(id, date, from, side, data, null, additionalContent, subject, null, answeredMsgId, answeredMsgDate, null, mentions);
        message.type = message.isAdaptiveCard() ? MessageType.FORM : MessageType.CHAT;
        return message;
    }

    public static createFromMsg(origMsg: Message): Message {
        const message = Message.create(origMsg.id, origMsg.date, origMsg.from, origMsg.side, origMsg.data);
        message.type = origMsg.type;
        message.additionalContent = origMsg.additionalContent;
        if (origMsg.fileId) { message.type = MessageType.FS; message.fileId = origMsg.fileId; message.fileName = origMsg.fileName; }
        if (origMsg.geoloc) { message.geoloc = origMsg.geoloc; }
        return message;
    }

    public static createFileSharingMessage(id: MessageID, date: Date, from, side: MessageSide, data: string, fileId: string | null, fileName, attachedInfos, geoloc: GeoLoc): Message {
        const msg = new Message(id, date, from, side, data, fileId, null, null, fileName, null, null, geoloc);
        msg.type = MessageType.FS;
        if (attachedInfos) {
            msg.attachedMsgId = attachedInfos.attachedMsgId;
            msg.attachIndex = attachedInfos.index;
            msg.attachNumber = attachedInfos.number;
        }
        return msg;
    }

    public static createFileMessage(id: MessageID, date: Date, from, side: MessageSide, data: string, status): Message {
        const message = new Message(id, date, from, side, data, status);
        message.type = MessageType.FILE;
        return message;
    }

    public static createWebRTCMessage(id: MessageID, date: Date, from, side: MessageSide, data: string, status): Message {
        const message = new Message(id, date, from, side, data, status);
        message.type = MessageType.WEBRTC;
        return message;
    }

    public static createPinMessage(id: MessageID, date: Date, from, side: MessageSide, data: string): Message {
        const message = new Message(id, date, from, side, data, null);
        message.type = MessageType.PIN;
        return message;
    }

    public static createShareRecapMessage(id: MessageID, date: Date, from, side: MessageSide, data: string, additionalContent: MessageAdditionalContent): Message {
        const message = new Message(id, date, from, side, data, undefined, additionalContent);
        message.type = MessageType.RECAP;
        return message;
    }

    public static createPollMessage(id: MessageID, date: Date, from, side: MessageSide, data: string, additionalContent: MessageAdditionalContent): Message {
        const message = new Message(id, date, from, side, data, null, additionalContent);
        message.type = MessageType.POLL;
        return message;
    }

    public static createBubbleAdminMessage(id: MessageID, date: Date, from, type, translatedMessage: string): Message {
        const adminMessage = translatedMessage ? translatedMessage : `${type}MsgRoom`;
        const message = new Message(id, date, from, MessageSide.ADMIN, adminMessage, null);
        message.type = MessageType.CHAT;
        return message;
    }

    public static createRecordingAdminMessage(id: MessageID, date: Date, from, cmd): Message {
        const message = new Message(id, date, from, MessageSide.ADMIN, cmd + "Recording", null);
        message.type = MessageType.RECORDING;
        return message;
    }

    public sendUpdateEvent(data: any = null): void {
        this.rxSubject.next(data);
    }

    public subscribe(callback: any): Subscription { return this.rxSubject.subscribe(callback); }

    public isTextModified(): boolean {
        return this.replaceMsgs.length > 0;
    }

    public isModified(): boolean {
        return this.modified || (this.replaceMsgs.length > 0 && this.getLastTextModified() !== '');
    }

    public isDeleted(): boolean {
        return this.deleted || (this.getLastTextModified().length === 0 && this.additionalContent === null && !this.fileName);
    }

    public getLastTextModified(): string {
        if (this.replaceMsgs.length > 0) { return this.replaceMsgs[this.replaceMsgs.length - 1].body; }
        return this.data;
    }

    public addReplaceMsg(messageId: MessageID, replacedMsgBody: string): void {
        const replacedMsg = ReplaceMsg.create(messageId, replacedMsgBody);
        this.replaceMsgs.push(replacedMsg);
    }

    public setReceiptStatus(receiptStatus: MessageStatus) {
        const receiptStatusRank = this.receiptStatusInfo[receiptStatus].rank;
        const inProgressRank = this.receiptStatusInfo[MessageStatus.IN_PROGRESS].rank;
        if (this.serverAckTimer && receiptStatusRank > inProgressRank) {
            clearTimeout(this.serverAckTimer); this.serverAckTimer = null;
        }
        if (receiptStatusRank > this.receiptStatusInfo[this.receiptStatus].rank) {
            this.receiptStatus = receiptStatus;
            this.sendUpdateEvent(RBEvent.create('receiptStatus', receiptStatus));
        }

        return this;
    }

    public getInfoForLogs() {
        const body = this.data.length ? this.data.substr(0, 1) + "***" + this.data.substr(this.data.length - 1) : "";
        return "Id " + this.id + ", receiptStatus " + this.receiptStatus + ", type " + this.type + ", side " + this.side + ", date " + this.date + " and data " + body;
    }

    public modify(stanzaData: any) {
        this.data = stanzaData.body ? stanzaData.body : '';
        this.additionalContent = stanzaData.additionalContent;
        if (this.type === MessageType.FORM && !this.hasAdditionalContent()) this.type = MessageType.CHAT;
        if (this.type === MessageType.CHAT && this.hasAdditionalContent() && this.isAdaptiveCard()) this.type = MessageType.FORM;
        if (stanzaData.mentions) this.mentions = stanzaData.mentions;
        this.modified = true;
        this.deleted = this.data !== null && this.data.length === 0 && (this.additionalContent === null || this.additionalContent === undefined);
        this.sendUpdateEvent();
    }

    //////////////////////////////////////////////////////////
    // Message Reaction STUFF *** START ***
    //////////////////////////////////////////////////////////

    public updateReaction(reaction: Reaction) {
        this.reactions.set(reaction.jid, reaction);
        this.updateReactionsCount();
        this.reactionsSubject.next({ reactions: this.reactions, addedReaction: reaction });

    }

    private updateReactionsCount() {
        this.reactionsCount.clear();
        for (const [__jid, reaction] of this.reactions) {
            const emojis = reaction.emojis;
            const splittedEmojis = Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(emojis), seg => seg.segment);
            splittedEmojis.forEach((emoji) => {
                let originalCount = this.reactionsCount.get(emoji) ? this.reactionsCount.get(emoji) : 0;
                this.reactionsCount.set(emoji, ++originalCount);
            });
        }
    }

    public handleReactions(reactions: Map<string, Reaction>) {
        for (const [jid, reaction] of reactions) {
            this.reactions.set(jid, reaction);
        }
        this.reactionsSubject.next({ reactions: this.reactions, addedReaction: null });
    }

    public handleReactionsCount(reactionsCount: Map<string, number>) {
        for (const [emojiReaction, count] of reactionsCount) {
            this.reactionsCount.set(emojiReaction, count);
        }
    }

    public getReactionsByUser(jid: string): string[] {
        const userReactions = this.reactions.get(jid);
        if (userReactions) {
            const emojis = userReactions.emojis;
            const splittedEmojis = Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(emojis), seg => seg.segment);
            return splittedEmojis;
        } return [];
    }

    public hasUserUsedEmojiReaction(jid: string, emoji: string): boolean {
        const userReaction = this.reactions.get(jid);
        return Boolean(userReaction?.emojis.includes(emoji));
    }

    //////////////////////////////////////////////////////////
    // Message Reaction STUFF *** END ***
    //////////////////////////////////////////////////////////

    public hasAdditionalContent(): this is MessageWithAdditional {
        return this.additionalContent != null;
    }

    public isMarkDown(): this is MessageWithAdditional<'text/markdown'> {
        return this.hasAdditionalContent() && this.additionalContent.type === 'text/markdown';
    }

    public isAdaptiveCard(): this is MessageWithAdditional<'form/json'> {
        return this.hasAdditionalContent() && this.additionalContent.type === 'form/json';
    }

    //public isAdaptiveCardResponse(): boolean { return this.hasAdditionalContent() && this.additionalContent.type === 'rainbow/json' && !this.isMessageback(this); }
    public isAdaptiveCardResponse(): this is MessageWithAdditional<'rainbow/json'> {
        return this.hasAdditionalContent() && this.additionalContent.type === 'rainbow/json';
    } // MCO GABRIEL IL FAUT QU'ON CAUSE

    public getReceiptStatusIconId(): string { return this.receiptStatusInfo[this.receiptStatus].icon; }

    public isMessageback(message: MessageWithAdditional): boolean {
        const parsedData = JSON.parse(message.additionalContent.message);
        return parsedData.rainbow && parsedData.rainbow.type === "messageBack";
    }

    public toJSON(): any {
        const json: any = {
            id: this.id,
            type: this.type,
            sendStatus: this.sendStatus,
            receiptStatus: this.receiptStatus,
            date: this.date,
            from: this.from.displayName,
            data: this.data,
        };
        if (this.data) json.data = this.data;
        if (this.additionalContent) json.additionalContent = this.additionalContent;
        if (this.urgency) json.urgency = this.urgency;
        if (this.fileId) json.fileId = this.fileId;
        if (this.fileName) json.fileName = this.fileName;
        if (this.answeredMsgId) json.answeredMsgId = this.answeredMsgId;
        return json;
    }
}

export class AnswerMessage {
    public name: string;
    public messageAnswered: Message;
    public answeredMessageData: any;

    constructor(
        name: string,
        messageAnswered: Message,
        answeredMessageData: any) {
        this.name = name;
        this.messageAnswered = messageAnswered;
        this.answeredMessageData = answeredMessageData;
    }
}
